import { Injectable } from '@angular/core';
import { ServiceInfoHttpService } from './service-info.http.service';
import { lastValueFrom } from 'rxjs';
import { objectToCamel } from 'ts-case-convert';
import { IServiceInfoDetail, ITown } from 'src/app/models';
import { IRoutePossible } from 'src/app/models/route/routePossible';
import { IServicesPanelFilters } from 'src/app/filters';

@Injectable({
  providedIn: 'root'
})
export class ServiceInfoService {

  constructor(private serviceInfoHttpService: ServiceInfoHttpService) {}

  async getServiceInfos(size: number, page: number, towns?: ITown[]) {
    const source$ = this.serviceInfoHttpService.getServiceInfos(size, page, towns);
    const services = await lastValueFrom(source$);
    const result = this.getData(services);
    return result;
  }

  async getServiceInfosSearch(
    size: number,
    page: number,
    towns?: ITown[],
    searchValue?: string,
    panelFilters?: IServicesPanelFilters,
  ) {
    const source$ = this.serviceInfoHttpService.getServiceInfosSearch(size, page, towns, searchValue, panelFilters);
    const services = await lastValueFrom(source$);
    const result = this.getData(services);
    return result;
  }

  async filterServiceInfo(page: number, size: number, clientIds?: number[], searchValue?: string) {
    const source$ = this.serviceInfoHttpService.filterServiceInfo(page, size, clientIds, searchValue);
    const services = await lastValueFrom(source$);
    const result = this.getData(services);
    return result;
  }

  async getServiceInfo(serviceId: number) {
    const source$ = this.serviceInfoHttpService.getServiceInfo(serviceId);
    const response = await lastValueFrom(source$);
    const service = objectToCamel(response as IServiceInfoDetail);
    return service;
  }

  async getServiceInfosByTowns(townIds: number[], page?: number, size?: number) {
    const source$ = this.serviceInfoHttpService.getServiceInfosByTowns(townIds, page, size);
    const response = await lastValueFrom(source$);
    const service = objectToCamel(response as []);
    return service;
  }

  async getServiceInfoTerrains(serviceInfoId: number) {
    const source$ = this.serviceInfoHttpService.getServiceInfoTerrains(serviceInfoId);
    const response = await lastValueFrom(source$);
    const terrainsArray = objectToCamel(response as []);
    return terrainsArray;
  }

  async createService(newService: IServiceInfoDetail) {
    const source$ = this.serviceInfoHttpService.createService(newService);
    const response = await lastValueFrom(source$);
    const service = objectToCamel(response as object);
    return service;
  }

  async updateService(serviceEdited: IServiceInfoDetail) {
    const source$ = this.serviceInfoHttpService.updateService(serviceEdited);
    const response = await lastValueFrom(source$);
    const service = objectToCamel(response as IServiceInfoDetail);
    return service;
  }

  async exportServiceFromGtfs(serviceIds: number[], routeIds?: number[]) {
    const source$ = this.serviceInfoHttpService.exportServiceFromGtfs(serviceIds, routeIds);
    const response = await lastValueFrom(source$);
    return response;
  }

  async downloadGtfs(endpoint: string) {
    const source$ = this.serviceInfoHttpService.downloadGtfs(endpoint);
    const response = await lastValueFrom(source$);
    return response;
  }

  async deleteService(serviceInfoId: number): Promise<string> {
    const source$ = this.serviceInfoHttpService.deleteService(serviceInfoId);
    return (await lastValueFrom(source$)) as string;
  }

  async getPossibleRoutes(serviceId: number, bookings: number[]) {
    const source$ = this.serviceInfoHttpService.getPossibleRoutes(serviceId, bookings);
    const response = await lastValueFrom(source$);
    const routes = objectToCamel(response as IRoutePossible[]);
    const result = this.getData(routes);
    return routes;
  }

  async getAvailabilities(serviceId: number, page?: number, size?: number) {
    const source$ = this.serviceInfoHttpService.getAvailablities(serviceId, page, size);
    const response = await lastValueFrom(source$);
    const availabilities = objectToCamel(response as object);
    const result = this.getData(availabilities);
    return result;
  }

  async updateSetupsConfig(serviceInfoId: number, config: any, options?: {
    deleteAvailabilities?: boolean;
    deleteComment?: string;
    sendNotification?: boolean;
    refund?: boolean;
    cancelRelated?: boolean;
    fullRefund?: boolean;
  }) {
    const source$ = this.serviceInfoHttpService.updateSetupsConfig(serviceInfoId, config, options);
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object);
  }

  getData(response: any) {
    return response.data;
  }
}
